import { useState } from 'react';

import { ChevronRight } from 'lucide-react';

import { GenericLineChart } from 'components/GenericLineChart';
import { LaneHistoryFromServiceChart } from 'components/LaneHistoryFromServiceChart';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LaneHistoryResponse, WeekLaneData } from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import {
  ReasoningDataSource,
  RecentLaneLoad,
} from 'types/agents/QuickQuoteAgent';
import { integrationNameMap } from 'types/enums/Integrations';
import {
  chartConfig,
  chartConfigPercentile,
} from 'utils/laneHistoryChartConfig';
import { cn } from 'utils/shadcn';

import {
  formatDollars,
  formatPercent,
  getDataSourceLabel,
  summarizeDataSource,
} from './reasoningHelpers';

const LABEL_CLASS =
  'text-[10px] font-semibold uppercase leading-normal tracking-wide';

const isServiceLaneHistory = (
  history: LaneHistoryResponse | LaneHistoryFromServiceResponse
): history is LaneHistoryFromServiceResponse =>
  Array.isArray((history as LaneHistoryFromServiceResponse).months);

// Renders lane history with the same charts the Quick Quote form uses.
function LaneHistoryChart({
  history,
}: {
  history: LaneHistoryResponse | LaneHistoryFromServiceResponse;
}) {
  if (isServiceLaneHistory(history)) {
    return (
      <LaneHistoryFromServiceChart
        months={history.months}
        last7Days={history.last7Days}
      />
    );
  }

  return (
    <>
      {Object.entries(history.resultsBySource ?? {}).map(([source, tiers]) => {
        const tier = tiers?.[0];
        if (
          !tier ||
          !tier.weeks.some((week) => week.maxRate > 0 || week.averageRate > 0)
        ) {
          return null;
        }
        // Fit the axis to the data (±10%) so a narrow band of rates doesn't read as flat.
        const rates = tier.weeks
          .flatMap((week) => [week.maxRate, week.lowestRate, week.averageRate])
          .filter((rate) => rate > 0);
        const yAxisMax = Math.ceil((Math.max(...rates) * 1.1) / 50) * 50;
        const yAxisMin = Math.max(
          0,
          Math.floor((Math.min(...rates) * 0.9) / 50) * 50
        );
        const xLabels = tier.weeks.map((week) => {
          const label = week.week ?? '';
          const separator = label.indexOf(' - ');
          return separator >= 0 ? label.slice(0, separator) : label;
        });
        return (
          <GenericLineChart
            key={source}
            data={tier.weeks}
            title={`${integrationNameMap[tier.source] ?? source} Lane History`}
            description={`Your loads on this lane, last ${tier.timeframe}`}
            chartConfig={
              tier.isPercentile ? chartConfigPercentile : chartConfig
            }
            xAxisDomain={xLabels}
            yAxisDomainMax={yAxisMax}
            yAxisDomainMin={yAxisMin}
            yAxisWidth={yAxisMax > 999 ? 45 : 40}
            thirdTooltipLabel='Loads'
            dataKeys={
              ['maxRate', 'averageRate', 'lowestRate'] as (keyof WeekLaneData)[]
            }
            isPercentile={tier.isPercentile}
          />
        );
      })}
    </>
  );
}

function RecentLoadsTable({ loads }: { loads: RecentLaneLoad[] }) {
  return (
    <table className='w-full table-fixed border-collapse text-left tabular-nums'>
      <caption className='sr-only'>Recent loads on this lane</caption>
      <thead>
        <tr className='border-b border-neutral-200 text-neutral-500'>
          <th className={cn('w-[27%] py-1', LABEL_CLASS)}>Load</th>
          <th className={cn('w-[15%] py-1', LABEL_CLASS)}>Pickup</th>
          <th className={cn('w-[19%] py-1 text-right', LABEL_CLASS)}>Paid</th>
          <th className={cn('w-[20%] py-1 text-right', LABEL_CLASS)}>Billed</th>
          <th className={cn('w-[19%] py-1 text-right', LABEL_CLASS)}>Margin</th>
        </tr>
      </thead>
      <tbody>
        {loads.map((load) => (
          <tr
            key={load.loadId}
            className='border-b border-neutral-100 text-[11px] text-neutral-700 last:border-b-0'
          >
            <td className='truncate py-1 pr-1'>{load.loadId}</td>
            <td className='py-1'>{formatPickupDate(load.pickupDate)}</td>
            <td className='py-1 text-right'>
              {formatDollars(load.carrierCost)}
            </td>
            <td className='py-1 text-right'>
              {formatDollars(load.customerRate)}
            </td>
            <td className='py-1 text-right'>{formatPercent(load.marginPct)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const formatPickupDate = (value: string): string => {
  // Date-only values are calendar days, not UTC midnight.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3])
      )
    : new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
};

function SourceDetails({ source }: { source: ReasoningDataSource }) {
  const hasRange = source.low !== undefined && source.high !== undefined;
  const loads = source.recentLoads ?? [];

  return (
    <Flex direction='col' gap='sm' className='w-full pb-2'>
      <dl className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 pl-4 text-[11px]'>
        {source.rate !== undefined && (
          <>
            <dt className='text-neutral-500'>Rate</dt>
            <dd className='text-neutral-800 tabular-nums'>
              {formatDollars(source.rate)}
            </dd>
          </>
        )}
        {hasRange && (
          <>
            <dt className='text-neutral-500'>Range</dt>
            <dd className='text-neutral-800 tabular-nums'>
              {formatDollars(source.low as number)} –{' '}
              {formatDollars(source.high as number)}
            </dd>
          </>
        )}
        {source.confidence !== undefined && (
          <>
            <dt className='text-neutral-500'>Confidence</dt>
            <dd className='text-neutral-800 tabular-nums'>
              {Math.round(source.confidence)}%
            </dd>
          </>
        )}
      </dl>
      {source.laneHistory && (
        // Let the shared chart's legend wrap at sidebar width.
        <div className='w-full [&_.recharts-legend-wrapper_.gap-4]:flex-wrap [&_.recharts-legend-wrapper_.gap-4]:gap-x-3 [&_.recharts-legend-wrapper_.gap-4]:gap-y-0.5'>
          <LaneHistoryChart history={source.laneHistory} />
        </div>
      )}
      {loads.length > 0 && <RecentLoadsTable loads={loads} />}
    </Flex>
  );
}

type DataSourcesProps = {
  sources: ReasoningDataSource[];
};

// "Data sources": one compact row per source Kit looked at, each expandable.
export function DataSources({ sources }: DataSourcesProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (sources.length === 0) {
    return null;
  }

  return (
    <Flex
      direction='col'
      className='w-full border-t border-neutral-200 pt-2'
      data-testid='agent-data-sources'
    >
      <Typography
        variant='body-xxs'
        weight='semibold'
        textColor='muted'
        className='uppercase leading-normal tracking-wide'
      >
        Data sources
      </Typography>
      <ul className='mt-0.5 w-full'>
        {sources.map((source, index) => {
          const isOpen = openIndex === index;
          const panelId = `agent-source-${index}-${source.kind}`;
          return (
            <li key={`${source.kind}-${source.label}`} className='w-full'>
              <button
                type='button'
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className='flex w-full cursor-pointer items-start gap-1 rounded-sm py-1 text-left hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
              >
                <ChevronRight
                  className={cn(
                    'mt-[3px] h-3 w-3 shrink-0 text-neutral-400 transition-transform',
                    isOpen && 'rotate-90'
                  )}
                  aria-hidden
                />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <Typography
                    as='span'
                    variant='body-xs'
                    className='text-[11.5px] leading-normal text-neutral-800'
                  >
                    {getDataSourceLabel(source)}
                    {source.usedAsBase && (
                      <Typography
                        as='span'
                        variant='body-xs'
                        textColor='muted'
                        className='text-[11px] leading-[inherit]'
                      >
                        {' '}
                        · Kit&apos;s buy rate
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    as='span'
                    variant='body-xs'
                    textColor='lessMuted'
                    className='text-[11px] leading-snug tabular-nums'
                  >
                    {summarizeDataSource(source)}
                  </Typography>
                </span>
              </button>
              {isOpen && (
                <div id={panelId}>
                  <SourceDetails source={source} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Flex>
  );
}
